import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { OnboardingWizard } from 'src/modules/onboarding/components/onboardingWizard';
import { getOnboardingSettings } from 'src/shared/services/regSettingService';
import { Toolbar, AppBar, Typography, IconButton, Button, Badge, Box, Menu, MenuItem, Divider, ListItemIcon, ListItemText } from '@mui/material';
import { Twirl as Hamburger } from 'hamburger-react';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { AccountCircle, Logout, KeyboardArrowDown, CloudDownload, Apartment } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import LogoImage from 'src/shared/components/atoms/logoImage';
import { Colors } from 'src/shared/components/styles/colors';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { SystemMessageContainer } from '../organisms/systemMessageContainer';

const HeaderToolbar = () => {
  const { sideMenuOpen, setSideMenuOpen, currentUser, currentMember, cleanLocalStorage } = useGlobals();
  const { t } = useTranslation(['common', 'start_view']);

  const showMyAssociation =
    !currentMember &&
    !!getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.ShowMyAssociation, 'false') &&
    !!currentUser &&
    currentUser?.associationCount > 0;

  const showMyDistrict =
    !currentMember &&
    !!currentUser &&
    currentUser.districtCount > 0 &&
    getMainSetting(currentUser.mainSettings ?? [], MainSettingsEnum.ShowMyDistrict, 'false');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState<null | HTMLElement>(null);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [onboardingEnabled, setOnboardingEnabled] = useState(false);
  const [onboardingRemaining, setOnboardingRemaining] = useState(0);
  const TOTAL_ONBOARDING_STEPS = 10;

  useEffect(() => {
    if (!currentUser || currentMember) return;
    const loadOnboarding = async () => {
      try {
        const res = await getOnboardingSettings();
        const enabledSetting = res.data.find((s) => s.keyName === 'onboardingEnabled');
        const isEnabled = enabledSetting?.bit === true;
        setOnboardingEnabled(isEnabled);

        const stepsSetting = res.data.find((s) => s.keyName === 'onboardingCompletedSteps');
        const completedCount = stepsSetting?.varchar ? stepsSetting.varchar.split(',').filter(Boolean).length : 0;
        setOnboardingRemaining(TOTAL_ONBOARDING_STEPS - completedCount);

        // Öppna automatiskt om aktiverat och inte alla steg är klara
        if (isEnabled && completedCount < TOTAL_ONBOARDING_STEPS) {
          setOnboardingOpen(true);
        }
      } catch {
        // Inga onboarding-inställningar
      }
    };
    loadOnboarding();
  }, [currentUser]);

  useEffect(() => {
    const handler = (e: Event) => {
      const completedCount = (e as CustomEvent).detail as number;
      setOnboardingRemaining(TOTAL_ONBOARDING_STEPS - completedCount);
    };
    const disabledHandler = () => setOnboardingEnabled(false);
    window.addEventListener('onboarding-steps-changed', handler);
    window.addEventListener('onboarding-disabled', disabledHandler);
    return () => {
      window.removeEventListener('onboarding-steps-changed', handler);
      window.removeEventListener('onboarding-disabled', disabledHandler);
    };
  }, []);

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const handleLogout = () => {
    cleanLocalStorage();
  };

  const menuId = 'primary-search-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
      sx={{
        '& .MuiMenu-list': {
          minWidth: '16rem !important',
        },
      }}
      MenuListProps={{ 'aria-labelledby': 'desktop-menu-button' }}
    >
      {showMyDistrict && (
        <MenuItem component={Link as any} to={RoutesEnum.OrganizationMyDistrict} onClick={handleMenuClose}>
          <ListItemIcon>
            <Apartment fontSize='small' />
          </ListItemIcon>
          <ListItemText>{t('common:headerToolbar.myDistrict')}</ListItemText>
        </MenuItem>
      )}
      {showMyAssociation && (
        <MenuItem component={Link as any} to={RoutesEnum.OrganizationMyAssociation} onClick={handleMenuClose}>
          <ListItemIcon>
            <Apartment fontSize='small' />
          </ListItemIcon>
          <ListItemText>{t('common:headerToolbar.myAssociation')}</ListItemText>
        </MenuItem>
      )}
      {(showMyDistrict || showMyAssociation) && <Divider />}
      {!currentMember && (
        <MenuItem component={Link as any} to={RoutesEnum.MyExports} onClick={handleMenuClose}>
          <ListItemIcon>
            <CloudDownload fontSize='small' />
          </ListItemIcon>
          <ListItemText>{t('common:headerToolbar.myExports')}</ListItemText>
        </MenuItem>
      )}
      {!currentMember && <Divider />}
      <MenuItem component={Link as any} to={RoutesEnum.Logout}>
        <ListItemIcon>
          <Logout fontSize='small' />
        </ListItemIcon>
        <ListItemText>{t('common:headerToolbar.logout')}</ListItemText>
      </MenuItem>
    </Menu>
  );

  const mobileMenuId = 'primary-search-account-menu-mobile';
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
      sx={{
        '& .MuiListItemIcon-root': { minWidth: 32, mr: 1 },
      }}
      MenuListProps={{ 'aria-labelledby': 'mobile-menu-button' }}
    >
      {showMyDistrict && (
        <MenuItem component={Link as any} to={RoutesEnum.OrganizationMyDistrict} onClick={handleMenuClose}>
          <ListItemIcon>
            <Apartment fontSize='small' aria-hidden />
          </ListItemIcon>
          <Typography>{t('common:headerToolbar.myDistrict')}</Typography>
        </MenuItem>
      )}

      {showMyAssociation && (
        <MenuItem component={Link as any} to={RoutesEnum.OrganizationMyAssociation} onClick={handleMenuClose}>
          <ListItemIcon>
            <Apartment fontSize='small' aria-hidden />
          </ListItemIcon>
          <Typography>{t('common:headerToolbar.myAssociation')}</Typography>
        </MenuItem>
      )}
      {(showMyDistrict || showMyAssociation) && <Divider />}
      {!currentMember && (
        <MenuItem component={Link as any} to={RoutesEnum.MyExports} onClick={handleMenuClose}>
          <ListItemIcon>
            <CloudDownload fontSize='small' aria-hidden />
          </ListItemIcon>

          <Typography>{t('common:headerToolbar.myExports')}</Typography>
        </MenuItem>
      )}
      {!currentMember && <Divider />}
      <MenuItem component={Link as any} to={RoutesEnum.Logout} onClick={handleLogout}>
        <ListItemIcon>
          <Logout aria-hidden />
        </ListItemIcon>
        <Typography>{t('common:headerToolbar.logout')}</Typography>
      </MenuItem>
    </Menu>
  );

  return (
    <>
      <AppBar
        position='fixed'
        sx={{
          zIndex: (thm) => thm.zIndex.appBar,
          color: Colors.HeaderTextColor,
          boxShadow: `
          rgba(0, 0, 0, 0.05) 0px 2px 1px -1px,
          rgba(0, 0, 0, 0.10) 0px 1px 1px 0px,
          rgba(0, 0, 0, 0.12) 0px 1px 3px 0px
          `,
          background: '#FFF',
        }}
        color='default'
      >
        <SystemMessageContainer />
        <Box sx={{ height: '50px' }}>
          <Toolbar component='nav' disableGutters sx={{ mt: -0.7 }}>
            <Box sx={{ ml: 1.5 }}>
              <Hamburger color={Colors.MenuIcon} size={25} toggled={sideMenuOpen} toggle={() => setSideMenuOpen(!sideMenuOpen)} />
            </Box>
            <LogoImage isSmall={false} />
            <Box sx={{ mx: 1, ml: '2rem' }} />
            <Box sx={{ flexGrow: 1 }} />
            {!currentMember && onboardingEnabled && (
              <Badge badgeContent={onboardingRemaining} color='error' sx={{ mr: 2 }}>
                <Button
                  id='onboarding-button'
                  variant='outlined'
                  size='small'
                  startIcon={<RocketLaunchIcon />}
                  onClick={() => setOnboardingOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  {t('common:headerToolbar.onboarding')}
                </Button>
              </Badge>
            )}
            <Typography variant='subtitle1' sx={{ fontWeight: 500, mr: 2, verticalAlign: 'middle' }} noWrap>
              {currentUser && currentUser.customerName}
            </Typography>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, pr: 2 }}>
              <Button
                id='desktop-menu-button'
                size='large'
                startIcon={<AccountCircle sx={{ fontSize: '1.7rem !important' }} />}
                aria-label='account of current user'
                aria-controls={menuId}
                aria-haspopup='true'
                onClick={handleProfileMenuOpen}
                color='inherit'
                sx={{ textTransform: 'initial' }}
                endIcon={<KeyboardArrowDown fontSize='small' />}
              >
                <Typography variant='subtitle2' sx={{ letterSpacing: 0 }}>
                  {currentMember
                    ? `${currentMember.firstName} ${currentMember.lastName}`
                    : currentUser && `${currentUser.firstName} ${currentUser.lastName}`}
                </Typography>
              </Button>
            </Box>
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                id='mobile-menu-button'
                size='large'
                aria-label='show more'
                aria-controls={mobileMenuId}
                aria-haspopup='menu'
                aria-expanded={isMobileMenuOpen ? 'true' : undefined}
                onClick={handleMobileMenuOpen}
                color='inherit'
              >
                <AccountCircle />
                <KeyboardArrowDown fontSize='small' />
              </IconButton>
            </Box>
          </Toolbar>
        </Box>
      </AppBar>
      {renderMobileMenu}
      {renderMenu}
      <OnboardingWizard open={onboardingOpen} onClose={() => setOnboardingOpen(false)} />
    </>
  );
};

export default HeaderToolbar;
